// ============================================================================
// ListFilterService - Shared Filter State for List Pages
// ============================================================================
// Provides text, date range, and status filtering shared between the right panel
// filter controls and list pages (orders, quotes, RMAs, etc.). Replaces V1's
// inline $scope filter logic. Auto-clears filters on route change.
// ============================================================================

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface DateRangeFilter {
  field: string;
  start: Date | null;
  end: Date | null;
}

@Injectable()
export class ListFilterService {
  private textFilterSubj = new BehaviorSubject<string>('');
  textFilter$ = this.textFilterSubj.asObservable();

  private dateRangeSubj = new BehaviorSubject<DateRangeFilter | null>(null);
  dateRange$ = this.dateRangeSubj.asObservable();

  private statusFilterSubj = new BehaviorSubject<string>('');
  statusFilter$ = this.statusFilterSubj.asObservable();

  constructor(private router: Router) {
    // Clear filters on route change
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe(() => this.clear());
  }

  get textFilter(): string { return this.textFilterSubj.getValue(); }
  get dateRange(): DateRangeFilter | null { return this.dateRangeSubj.getValue(); }
  get statusFilter(): string { return this.statusFilterSubj.getValue(); }

  setTextFilter(value: string): void {
    this.textFilterSubj.next(value);
  }

  setStatusFilter(value: string): void {
    this.statusFilterSubj.next(value);
  }

  setDateRange(preset: string, field: string): void {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = null;

    switch (preset) {
      case '30DAYS':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        break;
      case '60DAYS':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 60);
        break;
      case 'YTD':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'PRIORYEAR':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
      case 'ALL':
        this.dateRangeSubj.next(null);
        return;
    }

    this.dateRangeSubj.next({ field, start, end });
  }

  clear(): void {
    this.textFilterSubj.next('');
    this.dateRangeSubj.next(null);
    this.statusFilterSubj.next('');
  }

  /** Apply all filters to an array of items. Used by list state services. */
  applyFilters<T>(items: T[], statusField: string, dateField: string): T[] {
    let result = items;

    // Text filter
    const text = this.textFilter.toLowerCase();
    if (text) {
      result = result.filter((item: any) => {
        return Object.values(item).some(val =>
          val != null && String(val).toLowerCase().includes(text)
        );
      });
    }

    // Date range filter
    const dr = this.dateRange;
    if (dr && dr.field) {
      result = result.filter((item: any) => {
        const val = item[dr.field];
        if (!val) return false;
        const d = new Date(val);
        if (dr.start && d < dr.start) return false;
        if (dr.end && d > dr.end) return false;
        return true;
      });
    }

    // Status filter
    const status = this.statusFilter;
    if (status) {
      result = result.filter((item: any) => {
        const val = String(item[statusField] ?? '');
        return val === status;
      });
    }

    return result;
  }
}
